import React, { FC, useState } from 'react'
import { observer } from 'mobx-react'
import { RouteComponentProps } from 'react-router-dom'
import { IonContent, IonPage, useIonViewWillEnter } from '@ionic/react'
import QRCode from 'qrcode.react'
import { MktConfirmModal, MktEmpty, MktFooter, UseHeader } from '@cps'
import { ActivityAPI } from '@api/index'
import { storeMobx } from '@mobxStore'
import { copyText, modalLayer, toast, useHistoryNew } from '@utils'
import mktController from 'controllers/mktController'
import defaultImg from '@img/default-longMallProduct.png'
import {
  bookingStatusMap,
  formatDateTime,
  getActivityName,
  getCover,
  getEndAt,
  getSessionName,
  getStartAt,
  getTicketQuantity,
  isFreeActivity,
} from '../utils'
import './index.scss'

interface IProps extends IPage, RouteComponentProps<{ bookingId: string }, any, { bookingStatus?: string }> { }

const getRawTicketName = (item: any) => item?.ticketName || item?.name || item?.title || item?.activityTicketName || ''

const getDetailStoreId = (item: any) => {
  if (typeof item?.store === 'number' || typeof item?.store === 'string') return item.store
  return item?.store?.storeId ||
    item?.store?.id ||
    item?.storeId ||
    item?.activityStoreId ||
    item?.booking?.storeId ||
    item?.order?.storeId ||
    item?.infos?.storeId
}

const getDetailStoreName = (item: any) => {
  const storeName = item?.store?.storeName ||
    item?.store?.name ||
    item?.storeName ||
    item?.booking?.storeName ||
    item?.order?.storeName ||
    item?.infos?.storeName
  if (storeName) return storeName

  const storeId = getDetailStoreId(item)
  if (!storeId) return '--'
  const matchedStore: any = storeMobx.storeIdToStore?.[storeId] ||
    storeMobx.storeIdToStore?.[String(storeId)] ||
    storeMobx.storeIdToViewStore?.[storeId] ||
    storeMobx.storeIdToViewStore?.[String(storeId)] ||
    [...(storeMobx.viewStoreList || []), ...(storeMobx.storeList || []), storeMobx.info]
      .find((store: any) => String(store?.storeId || store?.id) === String(storeId))
  return matchedStore?.storeName || matchedStore?.name || '--'
}

const formatPaymentAmount = (value: any) => {
  const num = Number(value || 0)
  return `${i18n.t('common.currency')}${num.toFixed(num % 1 === 0 ? 0 : 2)}`
}

const formatRecordSessionTime = (start: any, end: any) => {
  const startText = formatDateTime(start)
  const endText = formatDateTime(end)
  if (startText === '--' && endText === '--') return '--'
  if (startText === '--') return endText
  if (endText === '--' || startText === endText) return startText
  if (startText.slice(0, 10) === endText.slice(0, 10)) return `${startText} - ${endText.slice(11)}`
  return `${startText} - ${endText}`
}

const paymentMethodKeyMap: Record<string, string> = {
  Alipay: 'pay.alipay',
  AlipayApp: 'pay.alipay',
  AlipayWeb: 'pay.alipay',
  ShengPayAlipayWeb: 'pay.alipay',
  ShengPayAlipayWebV2: 'pay.alipay',
  Wechat: 'pay.wechatPay',
  WeChat: 'pay.wechatPay',
  Weixin: 'pay.wechatPay',
  WechatPay: 'pay.wechatPay',
  WechatPayWeb: 'pay.wechatPay',
  ShengPayWechatWeb: 'pay.wechatPay',
  ShengPayWechatWebV2: 'pay.wechatPay',
  MemberBalance: 'pay.memberBalance',
  GiftCard: 'pay.giftCard',
  ApplePay: 'pay.applePay',
  PayPal: 'pay.paypal',
  Cash: 'pay.cash',
}

const formatPaymentMethods = (value: any) => {
  const methods = Array.isArray(value) ? value : value ? [value] : []
  return methods.map((method: any) => {
    const methodName = String(method || '')
    const textKey = paymentMethodKeyMap[methodName] ||
      (/wechat|weixin/i.test(methodName) ? 'pay.wechatPay' : '') ||
      (/alipay/i.test(methodName) ? 'pay.alipay' : '')
    return textKey ? i18n.t(textKey) : methodName
  }).filter(Boolean).join(i18n.t('mkt.activityReserve.paymentMethodSeparator')) || '--'
}

const normalizeRecordDetail = (res: any) => {
  const data = res?.data || res?.result || res || {}
  const booking = data?.booking || data?.record || data?.reservation || data?.order || {}
  return {
    ...data,
    ...booking,
    activity: booking?.activity || data?.activity,
    store: booking?.store || data?.store,
    session: booking?.session || booking?.schedule || data?.session || data?.schedule,
    ticket: booking?.ticket || data?.ticket,
  }
}

const ActivityReserveRecordDetail: FC<IProps> = ({ match, location }: IProps) => {
  const history = useHistoryNew()
  const { bookingId } = match.params
  const [info, setInfo] = useState<any>({})
  const [showCodePreview, setShowCodePreview] = useState<boolean>(false)
  const [previewUrl, setPreviewUrl] = useState<string>('')

  const getData = () => {
    ActivityAPI.getActivityReserveRecordDetail(bookingId).then((res: any) => {
      if (!res?.errors && !res?.error) setInfo(normalizeRecordDetail(res))
    }).catch(() => setInfo({}))
  }

  useIonViewWillEnter(() => {
    getData()
  })

  // 已过期由列表接口按时间归类，详情接口中的 bookingStatus 可能仍为 UNUSED。
  const status = location.state?.bookingStatus === 'EXPIRED' ? 'EXPIRED' : (info?.bookingStatus || info?.status || 'unused')
  const statusConfig = bookingStatusMap[status]
  const statusInfo = {
    text: statusConfig ? i18n.t(statusConfig.textKey) : (info?.statusName || '--'),
    className: statusConfig?.className || 'muted',
  }
  const qrCodeValue = info?.verification?.code || ''
  const displayCode = info?.bookingCode || ''
  const showVerifyCode = ['UNUSED', 'PENDING_VERIFY'].includes(String(status).toUpperCase()) && Boolean(qrCodeValue)
  const canRefund = info?.canRefund || info?.refundEnable
  const canCancel = info?.canCancel || info?.cancelEnable
  const sessionInfo = info?.session || info
  const ticketInfo = info?.ticket || info
  const memberInfo = info?.member || {}
  const storeAddress = info?.storeAddress || ''
  const isFree = isFreeActivity(info) || isFreeActivity(info?.activity)
  const ticketName = getRawTicketName(ticketInfo)
  const ticketQuantity = getTicketQuantity(info)
  const ticketDisplay = !isFree && ticketName
    ? i18n.t('mkt.activityReserve.ticketWithCount', { name: ticketName, count: ticketQuantity })
    : i18n.t('mkt.activityReserve.ticketCountValue', { count: ticketQuantity })
  const coverUrl = getCover(info) || defaultImg
  const remark = memberInfo?.remark || info?.remark || ''
  const orderInfo = info?.order || {}
  const ordersId = info?.ordersId || orderInfo?.ordersId || bookingId
  const orderAddTime = orderInfo?.addTime || info?.addTime || ''
  const orderPayTime = orderInfo?.payTime || info?.payTime || ''
  const paymentMethods = orderInfo?.paymentMethods || info?.paymentMethods || []
  const paymentMethodList = Array.isArray(paymentMethods) ? paymentMethods : paymentMethods ? [paymentMethods] : []
  const paidFee = orderInfo?.paidFee ?? info?.paidAmount ?? info?.paidFee
  const isZeroPayment = paymentMethodList.some((method: any) => String(method) === 'ZeroPay') ||
    (paidFee !== undefined && paidFee !== null && paidFee !== '' && Number(paidFee) === 0)
  const refundInfo = info?.refund || {}
  const isRefunded = Boolean(refundInfo?.refunded) || String(status).toUpperCase() === 'REFUNDED'

  const copyCode = () => {
    copyText(String(displayCode))
    toast({ message: i18n.t('mkt.activityReserve.copySuccess') })
  }

  const copyOrderNumber = () => {
    copyText(String(ordersId))
    toast({ message: i18n.t('mkt.activityReserve.orderNumberCopied') })
  }

  const handleCancelOrRefund = () => {
    const isRefund = canRefund
    modalLayer(MktConfirmModal, {
      title: i18n.t('mkt.activityReserve.confirmAction'),
      content: isRefund ? i18n.t('mkt.activityReserve.confirmRefund') : i18n.t('mkt.activityReserve.confirmCancel')
    }, 'activityReserveConfirm').then(() => {
      const request = isRefund ? ActivityAPI.refundActivityReserve(bookingId) : ActivityAPI.cancelActivityReserve(bookingId)
      request.then((res: any) => {
        if (res?.error) {
          toast({ message: res.message || i18n.t('mkt.activityReserve.operationFailed') })
          return
        }
        toast({ message: isRefund ? i18n.t('mkt.activityReserve.refundSubmitted') : i18n.t('mkt.activityReserve.reservationCancelled') })
        getData()
      })
    }).catch(() => {})
  }

  return (
    <IonPage className='activity-reserve-record-detail-page'>
      <UseHeader title={i18n.t('mkt.activityReserve.recordDetailTitle')} onBack={() => mktController.goBack(history)}></UseHeader>
      <IonContent className='page-content'>
        {!info || Object.keys(info).length === 0 ? <MktEmpty /> : (
          <div className='record-detail-body'>
            <section className={`ticket-card${showVerifyCode ? ' has-code' : ''}`}>
              <div className='ticket-activity-main'>
                <div className='product-cover' style={{ backgroundImage: `url(${coverUrl})` }} onClick={() => setPreviewUrl(coverUrl)}></div>
                <div className='product-info'>
                  <div className={`status-banner ${statusInfo.className}`}>{statusInfo.text}</div>
                  <h3>{getActivityName(info)}</h3>
                  <p>{getSessionName(sessionInfo)}</p>
                </div>
              </div>
              <div className='ticket-schedule-info'>
                <div className='ticket-info-row time-row'>
                  <span>{i18n.t('mkt.activityReserve.activityTime')}</span>
                  <strong>{formatRecordSessionTime(getStartAt(sessionInfo), getEndAt(sessionInfo))}</strong>
                </div>
                <div className='ticket-info-row location-row'>
                  <span>{i18n.t('mkt.activityReserve.store')}</span>
                  <div>
                    <strong>{getDetailStoreName(info)}</strong>
                    {storeAddress && <small>{storeAddress}</small>}
                  </div>
                </div>
              </div>
              {showVerifyCode && (
                <div className='ticket-code-section'>
                  <div className='ticket-perforation'></div>
                  <div className='qrcode-wrap' onClick={() => setShowCodePreview(true)}>
                    <QRCode value={String(qrCodeValue)} size={168} level='H' />
                  </div>
                  {displayCode && (
                    <div className='verify-code' onClick={copyCode}>
                      <span>{displayCode}</span>
                      <em>{i18n.t('mkt.activityReserve.copy')}</em>
                    </div>
                  )}
                </div>
              )}
            </section>

            <section className='info-section reservation-info-section'>
              <h3>{i18n.t('mkt.activityReserve.reservationInfo')}</h3>
              <div className='row ticket-row'><span>{i18n.t('mkt.activityReserve.ticket')}</span><strong>{ticketDisplay}</strong></div>
              <div className='row'><span>{i18n.t('mkt.activityReserve.registrant')}</span><strong>{memberInfo?.name || info?.registrantName || info?.name || '--'}</strong></div>
              <div className='row'><span>{i18n.t('mkt.activityReserve.mobile')}</span><strong>{memberInfo?.phone || info?.mobile || info?.phone || '--'}</strong></div>
              {String(remark).trim() && <div className='row remark-row'><span>{i18n.t('mkt.activityReserve.remark')}</span><strong>{remark}</strong></div>}
            </section>

            <section className='info-section payment-info-section'>
              <h3>{i18n.t('mkt.activityReserve.paymentInfo')}</h3>
              <div className='row order-number-row'>
                <span>{i18n.t('mkt.activityReserve.orderNumber')}</span>
                <div className='order-number-value'>
                  <strong>{ordersId || '--'}</strong>
                  {ordersId && <button type='button' onClick={copyOrderNumber}>{i18n.t('mkt.activityReserve.copy')}</button>}
                </div>
              </div>
              <div className='row'><span>{i18n.t('mkt.activityReserve.createdAt')}</span><strong>{orderAddTime || '--'}</strong></div>
              <div className='row'><span>{i18n.t('mkt.activityReserve.paidAt')}</span><strong>{orderPayTime || '--'}</strong></div>
              {!isZeroPayment && <div className='row'><span>{i18n.t('mkt.activityReserve.paymentMethod')}</span><strong>{formatPaymentMethods(paymentMethods)}</strong></div>}
              <div className='row'><span>{i18n.t('mkt.activityReserve.paymentAmount')}</span><strong>{formatPaymentAmount(paidFee)}</strong></div>
            </section>

            {isRefunded && (
              <section className='info-section refund-info-section'>
                <h3>{i18n.t('mkt.activityReserve.refundInfo')}</h3>
                <div className='row'><span>{i18n.t('mkt.activityReserve.refundTime')}</span><strong>{refundInfo?.refundTime || '--'}</strong></div>
                <div className='row'><span>{i18n.t('mkt.activityReserve.refundReason')}</span><strong>{refundInfo?.reason || '--'}</strong></div>
              </section>
            )}

          </div>
        )}
      </IonContent>
      {previewUrl && (
        <div className='image-preview-mask' onClick={() => setPreviewUrl('')}>
          <img src={previewUrl} alt='' onClick={(e: React.MouseEvent<HTMLImageElement>) => e.stopPropagation()} />
        </div>
      )}
      {showVerifyCode && showCodePreview && (
        <div className='qrcode-preview-mask' onClick={() => setShowCodePreview(false)}>
          <div className='qrcode-preview-card' onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
            <QRCode value={String(qrCodeValue)} size={300} level='H' />
            {displayCode && (
              <div className='verify-code preview-code' onClick={copyCode}>
                <span>{displayCode}</span>
                <em>{i18n.t('mkt.activityReserve.copy')}</em>
              </div>
            )}
          </div>
        </div>
      )}
      {(canRefund || canCancel) && (
        <MktFooter className='record-detail-footer'>
          <button onClick={handleCancelOrRefund}>{canRefund ? i18n.t('mkt.activityReserve.applyRefund') : i18n.t('mkt.activityReserve.cancelReservation')}</button>
        </MktFooter>
      )}
    </IonPage>
  )
}

export default observer(ActivityReserveRecordDetail)
